import React from 'react'
import { Button, Icon, Popover, Dialog } from '@equinor/eds-core-react'
import { close } from '@equinor/eds-icons'
import styled from 'styled-components'
import { baseFont, ClosedStyledLabel, EdsStyledLabel } from '@/styles/layout'
import { useWindowWidth } from '@/hooks/useWindowWidth'

interface ResponsiveFilterDialogProps {
    open: boolean
    onClose: () => void
    anchor: HTMLElement | null
    title: string
    children: React.ReactNode
}

const ResponsiveFilterDialog: React.FC<ResponsiveFilterDialogProps> = ({ open, onClose, anchor, title, children }) => {
    const windowWidth = useWindowWidth()

    if (!open) return null

    if (anchor == null) {
        return (
            <StyledDialog open={open} onClose={onClose} isDismissable>
                <button
                    tabIndex={-1}
                    style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', opacity: 0 }}
                />
                <Header>
                    <EdsStyledLabel group="heading" variant={windowWidth <= 1024 ? 'h4' : 'h5'}>
                        {title}
                    </EdsStyledLabel>
                    <CloseButton as="Link" onClick={onClose}>
                        {windowWidth < 1024 && <ClosedStyledLabel>Close</ClosedStyledLabel>}
                        <StyledCloseIcon data={close} size={24} />
                    </CloseButton>
                </Header>
                <Content>{children}</Content>
            </StyledDialog>
        )
    }

    return (
        <Popover open={open} anchorEl={anchor} onClose={onClose}>
            <Popover.Header>
                <Popover.Title>{title}</Popover.Title>
                <Button
                    style={{ height: '32px', width: '32px' }}
                    variant="ghost_icon"
                    aria-label="Close popover"
                    onClick={onClose}
                >
                    <Icon data={close} size={24} />
                </Button>
            </Popover.Header>
            <Popover.Content>{children}</Popover.Content>
        </Popover>
    )
}

const StyledDialog = styled(Dialog)`
    display: flex;
    flex-direction: column;
    width: 638px;
    height: auto;
    overflow-x: hidden;

    @media (max-width: 1024px) {
        position: fixed;
        left: 0;
        right: 0;
        bottom: 82px;
        top: auto;
        height: auto;
        max-height: calc(100vh - 64px - 75px - 64px - 16px - 16px - 16px - 8px);
        overflow-y: auto;
        overflow-x: hidden;
        overscroll-behavior: contain;
        -webkit-overflow-scrolling: touch;
        width: 100%;
        background: #ffffff;
        box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.15);
        transform: none;
        border-radius: 0px;
    }
`

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-left: 16px;

    button {
        background: none;
        border: none;
        cursor: pointer;
    }
    @media (max-width: 1024px) {
        color: #1d1d1d;
        margin-left: 0;
        margin-right: 0;
        padding-left: 16px;
        padding-right: 16px;
        padding-bottom: 12px;
        border-bottom: 1px solid #dcdcdc;
    }
`

const Content = styled.div`
    margin: 0rem 0;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    flex-grow: 1;
    margin-bottom: 16px;
`

const CloseButton = styled(Button)`
    ${baseFont}
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px;
    text-decoration: none;
    background: none;
    border: none;
    cursor: pointer;
    color: #007079;

    &:hover {
        background: #eaeaea;
    }

    @media (max-width: 1024px) {
        color: #1f6369;

        &:hover {
            background: #f5f5f5;
        }
    }
`

const StyledCloseIcon = styled(Icon)`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #007079;

    @media (max-width: 1024px) {
        color: #1f6369;
    }
`

export default ResponsiveFilterDialog
